// A coarse read on a boss fight, for the Bosses page and the Boss Ladder: your team's Power against the boss's, as
// one of three words. It is deliberately not a win chance. The exact fight (shared/boss-duel.ts) has crits, damage rolls
// and cooldown timing, and players should theorycraft their teams and types, not read off an answer.
//
// The team is read as one fighter, the way the duel plays it (everyone hits, the boss's blows land on one at a time):
//   damage = Σ dps  × (its level ÷ the boss's)² × its best element on the boss
//   bulk   = Σ bulk × (its level ÷ the boss's)² ÷ the boss's best element on it
//   team Power = 1.048 × ∛(damage × bulk), the same scale as shared/power.ts
//   ratio  = team Power ÷ the boss's Power (HP multiplier included)
//   Outmatched under OUTMATCHED_BELOW, Favoured from FAVOURED_FROM, Even between.
// The level edge (shared/combat.ts) is (a ÷ d)² on its blows and the inverse on the boss's blows on it, hence the
// squares. Halls keepers' bonus counts, as it does against bosses.
import { SPECIES } from './species.ts';
import { ELEMENT_IDS, matchup, type Element } from './elements.ts';
import { POWER_SCALE, fighterPower, powerParts } from './power.ts';
import { bossFighter, bossHpMult, creatureFighter } from './boss-duel.ts';
import type { CreatureDTO } from './protocol.ts';

export type Verdict = 'outmatched' | 'even' | 'favoured';
export const VERDICT_NAME: Record<Verdict, string> = { outmatched: 'Outmatched', even: 'Even', favoured: 'Favoured' };
/**
 * Calibrated on practice runs of random teams (tests/estimate.test.ts): below 0.95 a team almost never wins, from
 * 1.3 it almost always does, and in between it is a real gamble (about 10% to 90%) that team and type choices swing.
 */
export const OUTMATCHED_BELOW = 0.95;
export const FAVOURED_FROM = 1.3;

/** A boss's Power as it fights: its HP is bossHpMult times a creature's. */
export function bossPower(id: string, level: number): number {
  const f = bossFighter(id, level);
  return fighterPower({ ...f, stats: { ...f.stats, hp: f.stats.hp * bossHpMult(id) } });
}

/** Type edge of one creature on a boss: its best element against the boss, over the boss's best element against it. */
export function typeEdge(species: string, bossId: string): { off: number; def: number } {
  const mine = SPECIES[species].elements as readonly Element[], theirs = SPECIES[bossId].elements as readonly Element[];
  return { off: Math.max(...mine.map((e) => matchup(e, theirs))), def: Math.max(...theirs.map((e) => matchup(e, mine))) };
}

/** Elements that hit this boss hard (×1.5 or more on the base chart). */
export function bossWeakTo(bossId: string): Element[] {
  const theirs = SPECIES[bossId].elements as readonly Element[];
  return ELEMENT_IDS.filter((e) => matchup(e, theirs) > 1);
}

export interface Estimate { team: number; boss: number; ratio: number; verdict: Verdict }

export function verdictOf(ratio: number): Verdict {
  return ratio < OUTMATCHED_BELOW ? 'outmatched' : ratio >= FAVOURED_FROM ? 'favoured' : 'even';
}

/** The estimate for a team against a boss at a level. halls: the elements whose hall the trainer keeps. */
export function bossEstimate(team: readonly CreatureDTO[], bossId: string, level: number, halls?: readonly string[]): Estimate {
  const boss = bossPower(bossId, level);
  let dmg = 0, bulk = 0;
  for (const c of team) {
    const p = powerParts(creatureFighter(c, halls));
    const edge = (c.level / level) ** 2, te = typeEdge(c.species, bossId);
    dmg += p.dps * edge * te.off;
    bulk += (p.bulk * edge) / te.def;
  }
  const teamP = POWER_SCALE * Math.cbrt(Math.max(0, dmg * bulk));
  const ratio = boss > 0 ? teamP / boss : 0;
  return { team: Math.round(teamP), boss, ratio, verdict: team.length ? verdictOf(ratio) : 'outmatched' };
}

